import { DiscoveringProfileCard } from './DiscoveringProfileCard';
import { Box, Button, Typography } from '@mui/material';
import { useCommunityDiscover } from '../hook/useCommunityDiscover';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ProfileInfoDialog } from './ProfileInfoDialog';
import { FormattedMessage } from 'react-intl';
import {
  useCancelPeerProfile,
  useQuickConnectPeerProfile,
} from '../hook/useQuickConnectPeerProfile';
import { useFetchDiscoveringProfiles } from '../hook/useFetchDiscoveringProfiles';
import { LoadingIndicator } from '@xolv-mybrightlink/common';
import { CommunityFilterStore } from '../hook/useCommunityFilter';
import { DiscoverProfileDispatchProvider } from '../provider/DiscoverProfileDispatchProvider';

type DiscoveringProfileSectionProps = {
  filterStore: CommunityFilterStore;
};

export const DiscoveringProfileSection = ({
  filterStore: communityFilterStore,
}: DiscoveringProfileSectionProps) => {
  const { discoverStore, dispatch } = useCommunityDiscover();
  const [selectedProfileGuid, setSelectedProfileGuid] = useState<string | null>(
    null,
  );
  const [loadingProfile, setLoadingProfile] = useState(false);
  const quickConnectOperation = useQuickConnectPeerProfile(dispatch);
  const cancelConnectOperation = useCancelPeerProfile(dispatch);
  const fetchDiscoveringProfile = useFetchDiscoveringProfiles();
  const isSelectedProfileHasConnection = useMemo(
    () =>
      discoverStore.peerProfiles.some(
        (profile) =>
          profile.guid === selectedProfileGuid && profile.hasConnection,
      ),
    [discoverStore.peerProfiles, selectedProfileGuid],
  );
  const handleFetchProfile = useCallback(async () => {
    if (!communityFilterStore.isDraft) {
      setLoadingProfile(true);
      await fetchDiscoveringProfile(communityFilterStore.filters, dispatch);
      setLoadingProfile(false);
    }
  }, [communityFilterStore, dispatch, fetchDiscoveringProfile]);

  useEffect(() => {
    handleFetchProfile();
  }, [handleFetchProfile]);

  const onSelectProfile = (profileGuid: string) => {
    console.debug(`Select profile ${profileGuid}`);
    setSelectedProfileGuid(profileGuid);
  };

  let selectedProfileActionElement = null;
  if (selectedProfileGuid) {
    if (isSelectedProfileHasConnection) {
      selectedProfileActionElement = (
        <Button
          onClick={() => cancelConnectOperation(selectedProfileGuid)}
          variant='outlined'
          color='error'
          disableElevation
          sx={{
            px: 10,
            py: 1.5,
            borderRadius: 7.5,
          }}
        >
          <FormattedMessage id='common.cancel' />
        </Button>
      );
    } else {
      selectedProfileActionElement = (
        <Button
          onClick={() => quickConnectOperation(selectedProfileGuid)}
          variant='contained'
          disableElevation
          sx={{
            px: 10,
            py: 1.5,
            borderRadius: 7.5,
          }}
        >
          <FormattedMessage id='common.connect' />
        </Button>
      );
    }
  }

  if (loadingProfile) {
    return (
      <Box sx={{ display: 'flex', height: '50vh' }}>
        <LoadingIndicator fullHeight />
      </Box>
    );
  }

  if (discoverStore.error) {
    return <ErrorCommunityDiscover onClickRetry={handleFetchProfile} />;
  }

  if (discoverStore.isFetched && discoverStore.peerProfiles.length === 0) {
    return <EmptyPeerProfile onClickRetry={handleFetchProfile} />;
  }

  return (
    <DiscoverProfileDispatchProvider discoverDispatch={dispatch}>
      <Box sx={{ display: 'flex', gap: 10, mt: 5, flexWrap: 'wrap' }}>
        {discoverStore.peerProfiles
          .filter((profile) => !profile.hasConnection)
          .map((profile) => (
            <DiscoveringProfileCard
              key={profile.id}
              profile={profile}
              onSelectProfile={onSelectProfile}
            />
          ))}
        {!!selectedProfileGuid && (
          <ProfileInfoDialog
            openDialog={!!selectedProfileGuid}
            onCloseDialog={() => setSelectedProfileGuid(null)}
            peerProfileGuid={selectedProfileGuid}
            actionElement={selectedProfileActionElement}
          />
        )}
      </Box>
    </DiscoverProfileDispatchProvider>
  );
};

type ErrorCommunityDiscoverProps = {
  onClickRetry: () => void;
};

const ErrorCommunityDiscover = ({
  onClickRetry,
}: ErrorCommunityDiscoverProps) => {
  return (
    <Box
      sx={{
        mt: 25,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <img src='/public/assets/images/dog_error.png' width='80%' />
      <Typography textAlign='center' color='#626a7a'>
        <FormattedMessage
          id='common.error.fetch.fail'
          values={{ br: <br /> }}
        />
      </Typography>
      <Button
        disableElevation
        onClick={onClickRetry}
        variant='contained'
        sx={{ py: 4.5, px: 40, borderRadius: 7.5 }}
      >
        <FormattedMessage id='common.try-again' />
      </Button>
    </Box>
  );
};

type EmptyPeerProfileProps = {
  onClickRetry: () => void;
};

const EmptyPeerProfile = ({ onClickRetry }: EmptyPeerProfileProps) => {
  return (
    <Box
      sx={{
        mt: 25,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <img src='/public/assets/images/dog_empty.svg' width='80%' />
      <Typography textAlign='center' color='#626a7a'>
        <FormattedMessage
          id='community.peers.item.empty'
          values={{ br: <br /> }}
        />
      </Typography>
      <Button
        disableElevation
        onClick={onClickRetry}
        variant='contained'
        sx={{ py: 4.5, px: 40, borderRadius: 7.5 }}
      >
        <FormattedMessage id='common.try-again' />
      </Button>
    </Box>
  );
};
